/* eslint-disable import/no-anonymous-default-export */
const breakpoints = ["40em", "52em", "64em", "80em"] as any;

// aliases
breakpoints.sm = breakpoints[0];
breakpoints.md = breakpoints[1];
breakpoints.lg = breakpoints[2];
breakpoints.xl = breakpoints[3];

const fontSizes = ["12px", "14px", "16px", "20px", "28px", "32px", "40px"] as any;

// aliases
fontSizes.xs = fontSizes[0];
fontSizes.sm = fontSizes[1];
fontSizes.md = fontSizes[2];
fontSizes.lg = fontSizes[3];
fontSizes.xl = fontSizes[4];
fontSizes.xxl = fontSizes[5];
fontSizes.xxxl = fontSizes[6];

const space = ["4px", "8px", "16px", "24px", "32px", "40px"] as any;

// aliases
space.xs = space[0];
space.sm = space[1];
space.md = space[2];
space.lg = space[3];
space.xl = space[4];
space.xxl = space[5];

const colors = {
  white: "#ffffff",
  // Cohesive cool-neutral ramp (refined for crispness; brand colours
  // below are unchanged). Keys and lightness order preserved so existing
  // usages stay valid — gray[30] input borders, gray[100] hairlines, etc.
  gray: {
    5: "#F7F8FA",
    10: "#F1F4F8",
    20: "#D6DBE4",
    30: "#CCD3E0",
    40: "#AAB2C0",
    50: "#7B8494",
    60: "#5B6577",
    80: "#2D374B",
    100: "#E4E8EF",
  },
  green: {
    primary: "#40DA9E",
    50: "#DFF9EF",
  },
  orange: {
    primary: "#FDECE2",
    50: "#F48D53",
    // 1 digit off #F48D53 above — likely an unintentional near-duplicate,
    // not a deliberate second shade. Has a live consumer (risk-level
    // "medium" badges via MandateStatusContext) so left as-is rather than
    // silently merged; confirm with design before collapsing the two.
    100: "#F48D52",
  },
  blue: {
    primary: "#3158F2",
    primaryEmphasis: "#0d37da",
    10: "#CFD8FC",
    20: "#BCC9FB",
    50: "#DDE3FD",
    100: "#09093E",
  },
  red: {
    primary: "#F75E6E",
    50: "#FEE4E7",
    20: "#ff9ca5",
    40: "#F64153",
  },
  background: "#F1F4F8",
  darkBlue: "#09093E",
  pink: {
    100: "#FEECEE",
  },
  // Light border/tint used throughout the Profile tabs — distinct from
  // gray[10]/gray[20] above (close but not identical); kept separate so
  // adopting it doesn't shift any existing gray-scale usage.
  divider: "#EEF1F6",
  // Muted secondary text (and the odd subtle border) across the Profile
  // tabs. Distinct from gray[50]/gray[60] for the same reason as `divider`.
  muted: "#6B7280",
  // Clickable-link color used across Profile tabs — close to blue.primary
  // but not identical, so kept as its own token rather than merged.
  link: "#2F6BFF",
  // Low/medium/high risk-level badge colors, copy-pasted identically across
  // 4+ Settings/Properties pages before this token existed.
  risk: {
    low: { bg: "#E6F7EE", color: "#0F8F4E" },
    medium: { bg: "#FFF5E6", color: "#B06800" },
    high: { bg: "#FEECEC", color: "#C0392B" },
  },
};

export default {
  fontFamily: "'Inter', sans-serif",
  fontSizes,
  // Tightened for a crisper feel: small elements (buttons/inputs/badges)
  // use radii[0]; cards/containers use radii[1]. Was ["8px", "20px"].
  radii: ["6px", "12px"],
  space,
  // Subtle, brand-tinted elevation scale (tinted with darkBlue #09093E
  // rather than pure black for a softer, more modern shadow).
  shadows: {
    sm: "0 1px 2px rgba(9, 9, 62, 0.06)",
    md: "0 2px 8px rgba(9, 9, 62, 0.08)",
    lg: "0 12px 32px rgba(9, 9, 62, 0.12)",
    none: "none",
  },
  fontWeights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    base: 1.5,
    relaxed: 1.7,
  },
  letterSpacings: {
    tight: "-0.01em",
    normal: "0",
    wide: "0.04em",
  },
  // Ordered stacking scale — reach for these instead of another one-off
  // number when a dropdown/overlay/modal needs to sit above its neighbours.
  zIndices: {
    base: 1,
    dropdown: 10,
    sticky: 40,
    overlay: 50,
    modal: 9999,
    toast: 99999,
  },
  breakpoints,
  colors,
  // Usage-based aliases onto the hue-named palette above, so status/intent
  // UI (badges, pills, banners) can read `theme.semanticColors.error`
  // instead of the reader having to already know "red.primary means error".
  semanticColors: {
    success: colors.green.primary,
    warning: colors.orange[50],
    error: colors.red.primary,
    info: colors.blue.primary,
  },
  // Text colours for status badges. The semantic colours above are too light
  // to read as text on their own tints (1.6:1 to 2.6:1); these clear the 4.5:1
  // minimum on the badge backgrounds.
  statusText: {
    success: "#0B7A43",
    warning: "#955800",
    error: "#B83226",
    info: colors.blue.primaryEmphasis,
  },
};
